import React, { useMemo } from 'react';
import styles from 'CaseContextMenuButton.module.scss';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import Text from '../../../../Components/Base/Text/Text';
import {
	CaseTag,
	useAddCaseTagMutation,
	useCaseTagsQuery,
	useRemoveCaseTagMutation,
} from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import Loading, { LoadingSize } from '../../../../Components/Loading';
import useNotification from '../../../../Hooks/useNotification.hook';
import { IconType } from '../../../Base/Enums/IconType.enum';
import Icon from '../../../Base/Components/Icon/Icon';

interface Props {
	caseId: string;
}

interface ButtonProps {
	onClick: () => void;
	title: string;
	icon: React.ReactElement;
	notification?: boolean;
}

const MenuButton: React.FC<ButtonProps> = ({ onClick, title, icon }) => (
	<div className={styles.menuButton} onClick={onClick}>
		{icon}
		<Text tx={title} />
	</div>
);

const CaseContextMenuButton: React.FC<Props> = ({ caseId }) => {
	const { isOpen, onOpen, onClose } = useDisclose(false);
	const { data, isLoading, isFetching, refetch } = useCaseTagsQuery({
		where: { id: caseId },
	});
	const [addCaseTag] = useAddCaseTagMutation();
	const [removeCaseTag] = useRemoveCaseTagMutation();
	const { displayError, displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();

	const isUnderClinicalStudy: boolean = useMemo(() => {
		return (
			data?.caseTags?.some(item => item === CaseTag.ClinicalMonitoring) ?? false
		);
	}, [data]);

	const handleStartClinicalStudy = async () => {
		try {
			const result = await addCaseTag({
				where: { id: caseId },
				input: { tags: [CaseTag.ClinicalMonitoring] },
			}).unwrap();
			displayGQLError(result);
			if (result) {
				refetch();
				showNotificationSuccess({
					content:
						'component.caseContextMenu.clinicalMonitoringSuccessfullyStarted',
				});
				onClose();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleEndClinicalStudy = async () => {
		try {
			const result = await removeCaseTag({
				where: { id: caseId },
				input: { tags: [CaseTag.ClinicalMonitoring] },
			}).unwrap();
			displayGQLError(result);
			if (result) {
				refetch();
				showNotificationSuccess({
					content:
						'component.caseContextMenu.clinicalMonitoringSuccessfullyEnded',
				});
				onClose();
			}
		} catch (e) {
			displayError(e);
		}
	};

	if (isLoading || isFetching) {
		return <Loading size={LoadingSize.SMALL} />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.button} onClick={onOpen}>
				<Icon type={IconType.DOTS_HORIZONTAL_BLUE} />
			</div>
			{isOpen && (
				<div className={styles.menu}>
					<div className={styles.close} onClick={onClose} />
					{isUnderClinicalStudy ? (
						<MenuButton
							onClick={handleEndClinicalStudy}
							title='component.caseContextMenu.endClinicalMonitoring'
							icon={
								<Icon
									backgroundColor='#e9effb'
									type={IconType.USER_WITH_MAGNIFYING_GLASS_BLUE}
									badge
								/>
							}
						/>
					) : (
						<MenuButton
							onClick={handleStartClinicalStudy}
							title='component.caseContextMenu.startClinicalMonitoring'
							icon={
								<Icon
									backgroundColor='#e9effb'
									type={IconType.USER_WITH_MAGNIFYING_GLASS_BLUE}
								/>
							}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default CaseContextMenuButton;
